import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Container from '@mui/material/Container';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import formatCurrency from 'utils/formatCurrency';
import { PRICING } from '../../data/pricing-data';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import GradientHeading from 'components/GradientHeading';

const EventRegistration = () => {
    const theme = useTheme();

    const PriceRow = ({ label, price }) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.25,
                borderBottom: `1px dashed ${theme.palette.divider}`,
            }}
        >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography
                variant="body1"
                sx={{ fontWeight: 700, color: 'primary.main' }}
            >
                {formatCurrency(price)}
            </Typography>
        </Box>
    );

    return (
        <Box bgcolor={theme.palette.alternate.main}>
            <Container>
                <Box
                    sx={{
                        py: { xs: 6, md: 8 },
                        background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
                    }}
                >
                    <Container maxWidth="lg">
                        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
                            <GradientHeading label="Registration Information" />
                            <Typography
                                variant="body1"
                                sx={{ color: 'text.secondary', mt: 1 }}
                            >
                                All prices are in Indonesian Rupiah (IDR)
                            </Typography>
                        </Box>

                        <Grid container spacing={{ xs: 3, md: 4 }}>
                            {/* Symposium Only */}
                            <Grid item xs={12} md={6}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        borderRadius: 4,
                                        border: `1px solid ${theme.palette.grey[200]}`,
                                        boxShadow:
                                            '0 8px 24px rgba(0,0,0,0.08)',
                                        background:
                                            theme.palette.mode === 'light'
                                                ? 'white'
                                                : theme.palette.background
                                                      .paper,
                                    }}
                                >
                                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                mb: 2,
                                            }}
                                        >
                                            <Chip
                                                label="Simposium Only"
                                                color="primary"
                                                sx={{ fontWeight: 700 }}
                                            />
                                        </Box>
                                        <Divider sx={{ mb: 1.5 }} />
                                        {PRICING.symposium.map((item, idx) => (
                                            <PriceRow
                                                key={idx}
                                                label={item.label}
                                                price={item.price}
                                            />
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Workshop Only */}
                            <Grid item xs={12} md={6}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        borderRadius: 4,
                                        border: `1px solid ${theme.palette.grey[200]}`,
                                        boxShadow:
                                            '0 8px 24px rgba(0,0,0,0.08)',
                                        background:
                                            theme.palette.mode === 'light'
                                                ? 'white'
                                                : theme.palette.background
                                                      .paper,
                                    }}
                                >
                                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                mb: 2,
                                            }}
                                        >
                                            <Chip
                                                label="Workshop Only"
                                                color="secondary"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: 'white',
                                                }}
                                            />
                                        </Box>
                                        <Divider sx={{ mb: 1.5 }} />
                                        {PRICING.workshop.map((item, idx) => (
                                            <PriceRow
                                                key={idx}
                                                label={item.label}
                                                price={item.price}
                                            />
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Packages */}
                            <Grid item xs={12}>
                                <Box sx={{ mt: { xs: 2, md: 4 } }}>
                                    <Typography
                                        variant="h5"
                                        sx={{ fontWeight: 800, mb: 2 }}
                                    >
                                        Packages
                                    </Typography>
                                    {(() => {
                                        const professionLabels = Array.from(
                                            new Set(
                                                PRICING.packages.flatMap(
                                                    (pkg) =>
                                                        pkg.prices.map(
                                                            (p) => p.label,
                                                        ),
                                                ),
                                            ),
                                        );

                                        return (
                                            <TableContainer
                                                sx={{
                                                    borderRadius: 4,
                                                    border: `1px solid ${theme.palette.grey[200]}`,
                                                    boxShadow:
                                                        '0 8px 24px rgba(0,0,0,0.08)',
                                                    background:
                                                        theme.palette.mode ===
                                                        'light'
                                                            ? 'white'
                                                            : theme.palette
                                                                  .background
                                                                  .paper,
                                                    overflowX: 'auto',
                                                    p: { xs: 2, md: 3 },
                                                }}
                                            >
                                                <Table size="medium">
                                                    <TableHead>
                                                        <TableRow
                                                            sx={{
                                                                '& th': {
                                                                    py: 1.5,
                                                                },
                                                            }}
                                                        >
                                                            <TableCell
                                                                sx={{
                                                                    fontWeight: 800,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="h6"
                                                                    sx={{
                                                                        fontWeight: 800,
                                                                    }}
                                                                >
                                                                    Profession
                                                                </Typography>
                                                            </TableCell>
                                                            {PRICING.packages.map(
                                                                (pkg) => (
                                                                    <TableCell
                                                                        key={
                                                                            pkg.name
                                                                        }
                                                                        sx={{
                                                                            minWidth: 220,
                                                                            textAlign:
                                                                                'center',
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="h6"
                                                                            sx={{
                                                                                fontWeight: 800,
                                                                            }}
                                                                        >
                                                                            {
                                                                                pkg.name
                                                                            }
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                color: 'text.secondary',
                                                                                display:
                                                                                    'block',
                                                                                mt: 0.75,
                                                                            }}
                                                                        >
                                                                            {
                                                                                pkg.description
                                                                            }
                                                                        </Typography>
                                                                    </TableCell>
                                                                ),
                                                            )}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {professionLabels.map(
                                                            (label) => (
                                                                <TableRow
                                                                    key={label}
                                                                    sx={{
                                                                        borderBottom: `1px dashed ${theme.palette.divider}`,
                                                                        '& td': {
                                                                            py: 2,
                                                                        },
                                                                    }}
                                                                >
                                                                    <TableCell
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            {
                                                                                label
                                                                            }
                                                                        </Typography>
                                                                    </TableCell>
                                                                    {PRICING.packages.map(
                                                                        (
                                                                            pkg,
                                                                        ) => {
                                                                            const price =
                                                                                pkg.prices.find(
                                                                                    (
                                                                                        p,
                                                                                    ) =>
                                                                                        p.label ===
                                                                                        label,
                                                                                )?.price;
                                                                            return (
                                                                                <TableCell
                                                                                    key={`${pkg.name}-${label}`}
                                                                                    align="center"
                                                                                    sx={{
                                                                                        fontWeight: 700,
                                                                                        color: 'primary.main',
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        variant="body1"
                                                                                        sx={{
                                                                                            fontWeight: 700,
                                                                                            color: 'primary.main',
                                                                                        }}
                                                                                    >
                                                                                        {price !=
                                                                                        null
                                                                                            ? formatCurrency(
                                                                                                  price,
                                                                                              )
                                                                                            : '-'}
                                                                                    </Typography>
                                                                                </TableCell>
                                                                            );
                                                                        },
                                                                    )}
                                                                </TableRow>
                                                            ),
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        );
                                    })()}
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>
            </Container>
        </Box>
    );
};

export default EventRegistration;
